import * as React from 'react';
import moment from 'moment';
import { FaCog, FaEye, FaEyeSlash, FaRegCopy } from 'react-icons/fa';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  ComposedChart,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  BlockInfo,
  Button,
  Checkbox,
  FormGroup,
  Input,
  InputNumber,
  ItemListContent,
  ItemListItem,
  ItemListWrapper,
  DropDown,
  Modal,
  ModalContent,
  RotateLoader,
  SelectAdv,
  Tag,
  styled,
} from '@lib/components';
import { UI } from '../../../../../core/ui';
import { MCP_URL, mcpKeyScope } from '../../../../../mcp';

interface Props {
  items: T.Schema.API.APISchema[];
  restaurants: T.API.DashboardRestaurantsResponseItem[];
  canCreate: boolean;
  onCreate: () => void;
  fetchUsage: (data: T.API.McpUsageRequest) => Promise<T.API.McpUsageResponse>;
  fetchConnections: () => Promise<{ outcome: number; items?: T.API.McpConnection[]; message?: string }>;
  revokeConnection: (data: { id: string }) => Promise<{ outcome: number; message?: string }>;
  updateConnectionRestaurants: (
    data: T.API.McpConnectionRestaurantsRequest
  ) => Promise<T.API.McpConnectionRestaurantsResponse>;
  fetchSeries: (data: T.API.McpUsageSeriesRequest) => Promise<T.API.McpUsageSeriesResponse>;
  fetchLimits: (data: T.API.McpLimitsRequest) => Promise<T.API.McpLimitsResponse>;
  updateLimits: (data: T.API.McpLimitsUpdateRequest) => Promise<T.API.McpLimitsUpdateResponse>;
  canEditLimits: boolean;
}

/**
 * A gear menu of actions.
 *
 * Built on DropDown rather than DropDownList because that one sizes its
 * menu to its trigger - width: 100% of a wrapper containing only the gear
 * button - so any label longer than about ten characters wraps, and it
 * always opens rightward, which pushes it outside the card. DropDown takes
 * a width and a flow.
 */
/**
 * The connected-application list without ItemListContent's drop shadow.
 *
 * The list already sits inside the section's white card, so the shadow
 * drew a second box inside the first. The dividing borders between rows
 * are enough to read it as a list. Scoped here rather than changed in the
 * component, which other pages use standalone and do need the box.
 */
const FlatItemListContent = styled(ItemListContent)`
  box-shadow: none;
`;

/**
 * A connection row that does not pretend to be clickable.
 *
 * ItemListItem sets a pointer cursor and a hover highlight, which are
 * right for a list whose rows open something. These rows open nothing -
 * every action is in the gear menu - so the highlight promised a click
 * that never happened.
 */
const FlatItemListItem = styled(ItemListItem)`
  cursor: default;
  &:hover {
    background: white;
  }
`;

const MenuItem = styled('button')`
  display: block;
  width: 100%;
  text-align: left;
  border: 0;
  background: none;
  cursor: pointer;
  font-size: 14px;
  color: inherit;
  padding: 10px 12px;
  border-radius: 5px;
  white-space: nowrap;
  &:hover {
    background: rgba(0, 0, 0, 0.06);
  }
`;

/**
 * An icon on its own rather than an icon inside a button.
 *
 * A bordered grey box reads as an action in its own right, and there is
 * one on every connected-application row; what these open - a menu, or
 * the key beside them - is where the action is. So nothing is drawn
 * behind the icon, hovered or not: a hover background is the button shape
 * coming back, only on contact. The icon sits muted and comes up to full
 * strength instead. The button's own hover is a drop shadow, cast by
 * nothing when there is no button, hence disableHoverShadow at every call
 * site.
 */
const IconButton = styled(Button)`
  opacity: 0.55;
  &:hover {
    opacity: 1;
    background: none;
  }
`;

const ActionMenu: React.FC<{
  actions: Array<{ label: string; onSelect: () => void }>;
  title?: string;
}> = ({ actions, title }) => {
  const [active, setActive] = React.useState(false);

  return (
    <DropDown
      active={active}
      open={() => setActive(true)}
      close={() => setActive(false)}
      width={240}
      offset={4}
      flow="left"
      content={
        <div className="p-2">
          {actions.map(action => (
            <MenuItem
              key={action.label}
              type="button"
              onClick={() => {
                setActive(false);
                action.onSelect();
              }}
            >
              {action.label}
            </MenuItem>
          ))}
        </div>
      }
    >
      <IconButton
        type="button"
        color="transparent"
        disableHoverShadow={true}
        paddinglr={10}
        title={title || 'Options'}
      >
        <FaCog />
      </IconButton>
    </DropDown>
  );
};

const MASK = '••••••••••••••••••••';

function copyText(text: string) {
  if (!navigator.clipboard) {
    UI.notification.error('Clipboard unavailable');
    return;
  }
  navigator.clipboard.writeText(text).then(
    () => UI.notification.success('Copied'),
    () => UI.notification.error('Clipboard unavailable')
  );
}

const ReadOnlyInput: React.FC<{ value: string }> = ({ value }) => (
  <Input
    type="text"
    readOnly={true}
    value={value}
    className="cursor-text"
    onFocus={(e: React.FocusEvent<HTMLInputElement>) => e.target.select()}
  />
);

/** Read-only field with a copy button. `copyValue` copies something other than what is shown. */
const CopyRow: React.FC<{ value: string; copyValue?: string }> = ({ value, copyValue }) => (
  <div className="flex-l-r-center" style={{ gap: 8 }}>
    <ReadOnlyInput value={value} />
    <IconButton
      type="button"
      color="transparent"
      disableHoverShadow={true}
      paddinglr={10}
      title="Copy"
      onClick={() => copyText(copyValue !== undefined ? copyValue : value)}
    >
      <FaRegCopy />
    </IconButton>
  </div>
);

/**
 * "12 calls today, 129 in 30 days", as said under a key and in a
 * connected-application row.
 *
 * Both are the fixed 30 days /mcp/usage reports, not the window chosen
 * inside the usage modal - these lines sit on a page with no window
 * control, so the period is named in the text instead.
 */
function usageSummary(u: { calls_today: number; calls_30d: number; errors_30d: number }): string {
  if (u.calls_30d === 0) return 'not used yet';
  const counts = `${u.calls_today} calls today, ${u.calls_30d} in 30 days`;
  return u.errors_30d > 0 ? `${counts} · ${u.errors_30d} errors` : counts;
}

/**
 * The headline figures for a window, above the charts for the same window.
 *
 * Every label names the window it covers, because the window is a control
 * sitting a few pixels away: "129" under a 7/30/90 selector is a number
 * whose meaning changes without the label saying so.
 */
const UsageStats: React.FC<{ totals: T.API.McpUsageTotals; days: number }> = ({ totals, days }) => {
  const counts = [
    { label: 'Calls today', value: totals.calls_today },
    { label: `Calls (${days} days)`, value: totals.calls },
    { label: `Errors (${days} days)`, value: totals.errors },
  ];

  const topTools = Object.entries(totals.tools)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([tool, count]) => `${tool} (${count})`)
    .join(', ');

  return (
    <div>
      <div className="flex-l-r-center" style={{ gap: 8 }}>
        {counts.map(count => (
          <div key={count.label} className="bg-grey round-sm text-center p-tb-2" style={{ flex: 1 }}>
            <p className="big font-semi-bold m-b-1">{count.value}</p>
            <p className="small text-muted">{count.label}</p>
          </div>
        ))}
      </div>

      <p className="small m-t-3" title={totals.last_call_at ? moment(totals.last_call_at).format('LLL') : undefined}>
        <span className="text-muted">Last used: </span>
        {totals.last_call_at ? (
          <>
            {moment(totals.last_call_at).fromNow()} via {totals.last_client || 'unknown'}
          </>
        ) : (
          'Not used yet'
        )}
      </p>

      {topTools && (
        <div className="m-t-2">
          <p className="small text-muted m-b-1">Top tools</p>
          <p className="small">{topTools}</p>
        </div>
      )}
    </div>
  );
};

interface UsageState {
  loading: boolean;
  error: boolean;
  /** Keyed by api id. The endpoint returns every key in the organisation, zeroed when unused. */
  byId: Record<string, T.API.McpUsageItem>;
}

/**
 * Usage for every key in the organisation, in one request.
 *
 * Fetched for the whole set rather than for the selected key, because the
 * same response is what says which keys have reached the MCP server at
 * all - the filter above the picker reads it. Fetching per key would mean
 * two sources for one fact, and a request on every change of selection.
 */
function useKeyUsage(fetchUsage: Props['fetchUsage']) {
  const [state, setState] = React.useState<UsageState>({ loading: true, error: false, byId: {} });

  const load = React.useCallback(() => {
    setState({ loading: true, error: false, byId: {} });
    fetchUsage({})
      .then(res => {
        if (res.outcome || !res.items) {
          setState({ loading: false, error: true, byId: {} });
          return;
        }
        const byId: Record<string, T.API.McpUsageItem> = {};
        for (const item of res.items) byId[item.api_id] = item;
        setState({ loading: false, error: false, byId });
      })
      .catch(() => setState({ loading: false, error: true, byId: {} }));
  }, [fetchUsage]);

  React.useEffect(load, [load]);

  return { state, load };
}

/**
 * Whether this key has ever reached the MCP server.
 *
 * Nothing on a key records what it was made for - "Create MCP key" makes
 * an ordinary API key, and the same key works against the REST API - so
 * the only honest signal is traffic. The usage window is 30 days, so a key
 * last used before that reads as unused here, which is why the filter this
 * feeds can always be switched off.
 */
function usedWithMcp(usage: UsageState, apiId: string): boolean {
  const item = usage.byId[apiId];
  return !!item && item.last_call_at !== null;
}

/** Labels a key in the picker with the restaurants it can reach, e.g. "Live site - Acme Pizza". */
function keyOptionLabel(key: T.Schema.API.APISchema, restaurants: T.API.DashboardRestaurantsResponseItem[]): string {
  const scope = mcpKeyScope(key.restaurants);
  let hint = 'no restaurants';
  if (scope === 'single') {
    const found = restaurants.find(r => r._id === key.restaurants[0]);
    hint = found ? found.name : '1 restaurant';
  } else if (scope === 'multiple') {
    hint = `${key.restaurants.length} restaurants`;
  }
  return `${key.name} - ${hint}`;
}

/**
 * The host part of a client_id URL, when it is one - what an application
 * that sent no client_name is listed as.
 *
 * https with a path, matching what cw-mcp requires of a client_id before
 * it will fetch the metadata document behind it, so anything the server
 * would not have resolved is shown as the raw id rather than as a domain
 * it never stood behind.
 */
function clientHost(clientID: string): string | null {
  try {
    const url = new URL(clientID);
    if (url.protocol !== 'https:' || url.pathname.replace(/\//g, '') === '') return null;
    return url.host;
  } catch {
    return null;
  }
}

/** What to call this connection in the list. */
function clientLabel(item: T.API.McpConnection): string {
  return item.client_name || clientHost(item.client_id) || item.client_id;
}


/**
 * Per-restaurant call limits.
 *
 * Empty means "not set", which is how the default is restored - showing 0
 * would read as "no calls allowed", which is the opposite of what it does.
 */
const UsageLimits: React.FC<{
  restaurants: T.API.DashboardRestaurantsResponseItem[];
  fetchLimits: Props['fetchLimits'];
  updateLimits: Props['updateLimits'];
  canEdit: boolean;
}> = ({ restaurants, fetchLimits, updateLimits, canEdit }) => {
  const [restaurantId, setRestaurantId] = React.useState(restaurants[0]?._id || '');
  // Empty rather than 0: "not set" restores the default, where 0 would
  // read as "no calls allowed". InputNumber gives back number | '' so that
  // distinction survives without parsing a string back out.
  const [state, setState] = React.useState<{
    loading: boolean;
    daily: number | '';
    concurrency: number | '';
    maxDaily: number;
  }>({ loading: true, daily: '', concurrency: '', maxDaily: 0 });
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (!restaurantId) return;
    let live = true;
    setState(s => ({ ...s, loading: true }));
    fetchLimits({ restaurant_id: restaurantId }).then(
      res => {
        if (!live) return;
        if (res.outcome !== 0) {
          UI.notification.error('message' in res ? res.message : 'Could not load limits');
          setState(s => ({ ...s, loading: false }));
          return;
        }
        setState({
          loading: false,
          daily: res.daily_calls || '',
          concurrency: res.concurrency || '',
          maxDaily: res.max_daily_calls,
        });
      },
      () => live && setState(s => ({ ...s, loading: false }))
    );
    return () => {
      live = false;
    };
  }, [restaurantId, fetchLimits]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await updateLimits({
        restaurant_id: restaurantId,
        daily_calls: state.daily === '' ? 0 : state.daily,
        // Sent back unchanged. The field is no longer shown - concurrency
        // is an operational knob a restaurant owner cannot form a view on -
        // but a value already set must survive a save from this screen
        // rather than being silently cleared.
        concurrency: state.concurrency === '' ? 0 : state.concurrency,
      });
      if (res.outcome !== 0) {
        UI.notification.error('message' in res ? res.message : 'Could not save the limit');
        return;
      }
      UI.notification.success('Limit saved');
    } finally {
      setSaving(false);
    }
  };

  if (restaurants.length === 0) {
    return <BlockInfo>No restaurants to configure.</BlockInfo>;
  }

  return (
    <div>
      <FormGroup no_border={true} title="Restaurant">
        <SelectAdv
          type="single"
          options={restaurants.map(r => ({ value: r._id, label: r.name }))}
          value={restaurantId}
          onChange={(option: string) => setRestaurantId(option)}
        />
      </FormGroup>

      <FormGroup no_border={true} title="Calls per day" help={`Leave empty for the default. Maximum ${state.maxDaily || '-'}.`}>
        <InputNumber
          value={state.daily}
          disabled={!canEdit || state.loading}
          placeholder="Default"
          change={value => setState(s => ({ ...s, daily: value }))}
        />
      </FormGroup>

      {canEdit ? (
        <Button color="primary" size="xs" disabled={saving || state.loading} onClick={save}>
          {saving ? 'Saving...' : 'Save'}
        </Button>
      ) : (
        <p className="small text-muted">Only an account owner can change this.</p>
      )}
    </div>
  );
};


// Enough hues to tell a handful of restaurants apart without inventing a
// palette; beyond this the chart is unreadable anyway and the tooltip is
// what people read.
const SERIES_COLORS = ['#c8102e', '#1f77b4', '#2ca02c', '#ff7f0e', '#9467bd', '#8c564b', '#17becf', '#7f7f7f'];

const WINDOWS = [7, 30, 90];

// Bands beyond this are collapsed into one. Six restaurants already made
// the legend wrap and the stack hard to read.
const MAX_SERIES = 5;
const OTHER = 'Other restaurants';

/**
 * Lists only the restaurants with calls on the hovered day.
 *
 * The default tooltip prints every series, so a day when one restaurant
 * was used reads as six lines of zeros with the answer buried among them.
 */
/**
 * Totals per restaurant over the window, as proportional bars.
 *
 * The time series answers "how much, and when". This answers "who", which
 * is the question worth asking when traffic is occasional - a month of
 * empty days and one busy one says very little as a time series. Plain
 * markup rather than a second chart: it needs no colour matching and stays
 * readable at twenty restaurants, where a stack does not.
 */
const UsageTotals: React.FC<{ rows: Array<{ id: string; name: string; total: number }> }> = ({ rows }) => {
  if (rows.length === 0) return null;
  const max = rows[0].total;

  return (
    <div className="m-b-6">
      {rows.map(row => (
        <div key={row.id} className="m-b-2">
          <div className="flex-l-r-center" style={{ gap: 8 }}>
            <span className="small">{row.name}</span>
            <span className="small text-muted" style={{ fontVariantNumeric: 'tabular-nums' }}>
              {row.total}
            </span>
          </div>
          <div style={{ height: 6, borderRadius: 3, background: 'rgba(0,0,0,0.06)', marginTop: 3 }}>
            <div
              style={{
                height: '100%',
                borderRadius: 3,
                background: 'var(--accent, #c8102e)',
                /* Widths are relative to the busiest, so the shape of the
                   comparison survives whatever the absolute numbers are. */
                width: `${Math.max(2, (row.total / max) * 100)}%`,
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

const UsageTooltip: React.FC<{ active?: boolean; label?: string; payload?: any[] }> = ({ active, label, payload }) => {
  if (!active || !payload) return null;
  const rows = payload.filter(p => Number(p.value) > 0).sort((a, b) => Number(b.value) - Number(a.value));

  return (
    <div
      style={{
        background: 'var(--box-background, #fff)',
        border: '1px solid #dfdfdf',
        borderRadius: 5,
        padding: '8px 10px',
        fontSize: 12,
      }}
    >
      <p className="font-semi-bold m-b-1">{label}</p>
      {rows.length === 0 && <p className="text-muted">No calls</p>}
      {rows.map(row => (
        <p key={String(row.name)} style={{ color: row.color }}>
          {row.name}: {row.value}
        </p>
      ))}
    </div>
  );
};

/**
 * Calls per day for one key or connected application, stacked by
 * restaurant, with errors on the same axis.
 *
 * Errors are drawn as a count rather than a rate: one error in two calls
 * and five hundred in a thousand are not the same problem, and a ratio
 * hides which one you have.
 */
const UsageChart: React.FC<{
  subject: T.API.McpUsageSeriesRequest;
  title: string;
  fetchSeries: Props['fetchSeries'];
  onClose: () => void;
}> = ({ subject, title, fetchSeries, onClose }) => {
  const [days, setDays] = React.useState(30);
  const [state, setState] = React.useState<{ loading: boolean; error: boolean; data: T.API.McpUsageSeries | null }>({
    loading: true,
    error: false,
    data: null,
  });

  React.useEffect(() => {
    let live = true;
    setState(s => ({ ...s, loading: true, error: false }));
    fetchSeries({ ...subject, days })
      .then(res => {
        if (!live) return;
        if (res.outcome !== 0) {
          setState({ loading: false, error: true, data: null });
          return;
        }
        setState({
          loading: false,
          error: false,
          data: { restaurants: res.restaurants, series: res.series, totals: res.totals },
        });
      })
      .catch(() => live && setState({ loading: false, error: true, data: null }));
    return () => {
      live = false;
    };
    // subject is a fresh object each render; its identity is the id inside it.
  }, [subject.api_id, subject.grant_id, days, fetchSeries]);

  const data = state.data;
  const total = data ? data.series.reduce((n, p) => n + p.calls, 0) : 0;

  // Busiest first, so the biggest band sits at the bottom of each stack and
  // the legend reads in the order someone cares about.
  const ranked = data
    ? data.restaurants
        .map(r => ({
          ...r,
          total: data.series.reduce((n, p) => n + (p.by_restaurant[r.id] || 0), 0),
        }))
        .filter(r => r.total > 0)
        .sort((a, b) => b.total - a.total)
    : [];

  // Beyond a handful of bands a stack is unreadable and the legend wraps
  // over several lines, so the tail is collapsed into one.
  const series = ranked.slice(0, MAX_SERIES);
  const grouped = ranked.slice(MAX_SERIES);

  const anyErrors = data ? data.series.some(p => p.errors > 0) : false;

  // recharts wants one flat object per point, with a key per series.
  const points = data
    ? data.series.map(p => {
        const row: Record<string, string | number> = {
          date: moment(p.date).format('D MMM'),
          Errors: p.errors,
        };
        for (const r of series) {
          row[r.name] = p.by_restaurant[r.id] || 0;
        }
        if (grouped.length) {
          row[OTHER] = grouped.reduce((n, r) => n + (p.by_restaurant[r.id] || 0), 0);
        }
        return row;
      })
    : [];

  return (
    <Modal width={720} active={true} close={onClose}>
      <ModalContent>
        <h4 className="m-b-1">{title}</h4>
        <p className="small text-muted">
          Everything below covers the window you pick.
          {grouped.length > 0 && ` The daily chart shows the ${MAX_SERIES} busiest restaurants; the rest are grouped.`}
        </p>
      </ModalContent>
      <ModalContent>
        <div className="m-b-4" style={{ display: 'flex', gap: 6 }}>
          {WINDOWS.map(w => (
            <Button key={w} type="button" color={w === days ? 'primary' : 'white'} size="xs" onClick={() => setDays(w)}>
              {w} days
            </Button>
          ))}
        </div>

        {state.loading && <RotateLoader size={2} />}
        {!state.loading && state.error && <p className="small error-text">Couldn&apos;t load usage.</p>}
        {!state.loading && !state.error && total === 0 && (
          <BlockInfo>No calls in the last {days} days.</BlockInfo>
        )}
        {!state.loading && !state.error && total > 0 && data && (
          <>
            <div className="m-b-6">
              <UsageStats totals={data.totals} days={days} />
            </div>
            <p className="small font-semi-bold m-b-3">Calls by restaurant</p>
            <UsageTotals rows={ranked} />
            <p className="small font-semi-bold m-b-3">Calls per day</p>
          </>
        )}
        {!state.loading && !state.error && total > 0 && data && (
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={points} margin={{ top: 8, right: 8, bottom: 0, left: -20 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <ChartTooltip content={<UsageTooltip />} />
              {(series.length > 1 || grouped.length > 0) && <Legend wrapperStyle={{ fontSize: 12 }} />}
              {series.map((r, i) => (
                <Bar key={r.id} dataKey={r.name} stackId="calls" fill={SERIES_COLORS[i % SERIES_COLORS.length]} />
              ))}
              {grouped.length > 0 && <Bar dataKey={OTHER} stackId="calls" fill="#9aa0a6" />}
              {/* A flat line at zero reads as a feature rather than the
                  absence of one, so it is only drawn when there is
                  something to draw. */}
              {anyErrors && <Line type="monotone" dataKey="Errors" stroke="#d9534f" dot={false} strokeWidth={2} />}
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </ModalContent>
    </Modal>
  );
};

const ConnectedApps: React.FC<{
  fetchConnections: Props['fetchConnections'];
  revokeConnection: Props['revokeConnection'];
  updateConnectionRestaurants: Props['updateConnectionRestaurants'];
  fetchSeries: Props['fetchSeries'];
  restaurants: T.API.DashboardRestaurantsResponseItem[];
}> = ({ fetchConnections, revokeConnection, updateConnectionRestaurants, fetchSeries, restaurants }) => {
  const [state, setState] = React.useState<{ loading: boolean; error: boolean; items: T.API.McpConnection[] }>({
    loading: true,
    error: false,
    items: [],
  });
  // The connection awaiting confirmation, and whether its revoke is in
  // flight. Disconnecting cannot be undone - the application has to be
  // authorized again - so it is confirmed like deleting an API key.
  const [confirming, setConfirming] = React.useState<T.API.McpConnection | null>(null);
  const [revoking, setRevoking] = React.useState(false);
  // The connection whose restaurants are being edited, and the ticked ids.
  const [editing, setEditing] = React.useState<string | null>(null);
  const [chosen, setChosen] = React.useState<string[]>([]);
  const [savingRestaurants, setSavingRestaurants] = React.useState(false);
  const [charting, setCharting] = React.useState<T.API.McpConnection | null>(null);

  const load = React.useCallback(() => {
    setState(s => ({ ...s, loading: true, error: false }));
    fetchConnections()
      .then(res => {
        if (res.outcome || !res.items) {
          setState({ loading: false, error: true, items: [] });
          return;
        }
        setState({ loading: false, error: false, items: res.items });
      })
      .catch(() => setState({ loading: false, error: true, items: [] }));
  }, [fetchConnections]);

  React.useEffect(load, [load]);

  const saveRestaurants = (id: string) => {
    setSavingRestaurants(true);
    updateConnectionRestaurants({ id, restaurant_ids: chosen })
      .then(res => {
        if (res.outcome !== 0) {
          UI.notification.error('message' in res ? res.message : 'Could not save');
          return;
        }
        UI.notification.success('Access updated');
        setEditing(null);
        load();
      })
      .catch(() => UI.notification.error('Could not save'))
      .finally(() => setSavingRestaurants(false));
  };

  const revoke = () => {
    if (!confirming) return;
    setRevoking(true);
    revokeConnection({ id: confirming.id })
      .then(res => {
        setRevoking(false);
        if (res.outcome) {
          UI.notification.error(res.message || 'Could not disconnect that application');
          return;
        }
        UI.notification.success('Disconnected');
        setConfirming(null);
        load();
      })
      .catch(() => {
        setRevoking(false);
        UI.notification.error('Could not disconnect that application');
      });
  };

  if (state.loading) {
    return (
      <FormGroup no_border={true}>
        <div className="flex-center p-tb-4">
          <RotateLoader size={2} />
        </div>
      </FormGroup>
    );
  }
  if (state.error) {
    return (
      <FormGroup no_border={true}>
        <p className="small error-text m-b-2">Couldn&apos;t load connected apps.</p>
        <Button type="button" color="white" size="xs" onClick={load}>
          Retry
        </Button>
      </FormGroup>
    );
  }

  return (
    <FormGroup no_border={true}>
      {state.items.length === 0 && <BlockInfo>No applications are connected to your account.</BlockInfo>}

      {state.items.length > 0 && (
        <ItemListWrapper>
          <FlatItemListContent>
            {state.items.map(item => (
              <FlatItemListItem key={item.id}>
                {/* No horizontal padding: without the list's own box, the
                    rows should line up with the section heading rather
                    than sit indented inside a border that is not there. */}
                <div className="p-tb-3">
                  <p className="big font-semi-bold m-b-2">{clientLabel(item)}</p>
                  {editing === item.id ? (
                    <div className="m-b-2">
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 6 }}>
                        {restaurants.map(r => (
                          <Checkbox
                            key={r._id}
                            id={`mcp-restaurant-${item.id}-${r._id}`}
                            label={r.name}
                            checked={chosen.indexOf(r._id) !== -1}
                            onChange={() =>
                              setChosen(current =>
                                current.indexOf(r._id) === -1
                                  ? current.concat(r._id)
                                  : current.filter(id => id !== r._id)
                              )
                            }
                          />
                        ))}
                      </div>
                      <div className="m-t-3" style={{ display: 'flex', gap: 6 }}>
                        <Button
                          type="button"
                          color="primary"
                          size="xs"
                          disabled={savingRestaurants || chosen.length === 0}
                          onClick={() => saveRestaurants(item.id)}
                        >
                          {savingRestaurants ? 'Saving...' : 'Save'}
                        </Button>
                        <Button type="button" color="white" size="xs" onClick={() => setEditing(null)}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    /* The list of restaurants lives behind Restaurants in the
                       gear menu. A row that names six of them is mostly a
                       list of things that did not change, and it pushed the
                       one fact that does change - usage - off the fold.
                       An application reaching none of them is different: it
                       can do nothing at all, and that is worth a line. */
                    item.restaurants.length === 0 && <p className="small text-muted m-b-2">No restaurants</p>
                  )}
                  <p className="small text-muted" title={moment(item.created_at).format('LLL')}>
                    Connected {moment(item.created_at).fromNow()}
                    {item.usage && ` · ${usageSummary(item.usage)}`}
                  </p>
                </div>
                {/* One menu rather than three buttons. Two icons and a
                    text label read as three unrelated controls and crowd
                    the row, and disconnecting is safer a click deeper.

                    Hidden while this row is being edited: the menu's own
                    Restaurants action is what opened the editor, and it was
                    sitting on top of the last column of checkboxes. Usage and
                    Disconnect mid-edit would discard the selection silently. */}
                {editing !== item.id && (
                  <div className="flex-center">
                    <ActionMenu
                      actions={[
                        { label: 'Usage', onSelect: () => setCharting(item) },
                        {
                          label: 'Restaurants',
                          onSelect: () => {
                            setEditing(item.id);
                            setChosen(item.restaurant_ids || []);
                          },
                        },
                        { label: 'Disconnect', onSelect: () => setConfirming(item) },
                      ]}
                    />
                  </div>
                )}
              </FlatItemListItem>
            ))}
          </FlatItemListContent>
        </ItemListWrapper>
      )}

      {charting && (
        <UsageChart
          subject={{ grant_id: charting.id }}
          title={clientLabel(charting)}
          fetchSeries={fetchSeries}
          onClose={() => setCharting(null)}
        />
      )}

      <Modal width="sm" active={confirming !== null} close={() => setConfirming(null)}>
        <ModalContent>
          <h4 className="m-b-1">Disconnect Application</h4>
          <p className="underline">{confirming ? clientLabel(confirming) : ''}</p>
        </ModalContent>
        <ModalContent>
          <p className="lhp big">
            This application will immediately lose access to your restaurants and menus. To use it again you will have
            to connect and approve it from scratch.
          </p>
        </ModalContent>
        <ModalContent className="flex-right">
          <Button className="m-r-2 width100 max128" onClick={() => setConfirming(null)}>
            Cancel
          </Button>
          <Button className="width100 max128" color="primary" onClick={revoke}>
            {revoking && <RotateLoader size={2} color="white" />}
            {!revoking && 'Disconnect'}
          </Button>
        </ModalContent>
      </Modal>
    </FormGroup>
  );
};

export const MCPSection: React.FC<Props> = ({
  items,
  restaurants,
  canCreate,
  onCreate,
  fetchUsage,
  fetchConnections,
  revokeConnection,
  updateConnectionRestaurants,
  fetchSeries,
  fetchLimits,
  updateLimits,
  canEditLimits,
}) => {
  const [selectedId, setSelectedId] = React.useState<string | null>(items.length > 0 ? items[0]._id : null);
  const [showKey, setShowKey] = React.useState(false);
  const [keyCharting, setKeyCharting] = React.useState(false);
  const [showingRestaurants, setShowingRestaurants] = React.useState(false);
  const [onlyMcp, setOnlyMcp] = React.useState(true);
  const knownIdsRef = React.useRef(items.map(i => i._id));
  const { state: usage } = useKeyUsage(fetchUsage);

  // Follow a newly created key, and fall back to the first key when the selected one is deleted.
  React.useEffect(() => {
    const ids = items.map(i => i._id);
    const added = ids.filter(id => knownIdsRef.current.indexOf(id) === -1);
    knownIdsRef.current = ids;

    if (added.length > 0) {
      setSelectedId(added[added.length - 1]);
      return;
    }
    setSelectedId(current => (current && ids.indexOf(current) !== -1 ? current : ids[0] || null));
  }, [items]);

  React.useEffect(() => {
    setShowKey(false);
  }, [selectedId]);

  const selectedApi = items.find(i => i._id === selectedId) || null;
  const apiKey = selectedApi?.api_key || '';
  const shownKey = showKey ? apiKey : MASK;

  const scopedRestaurants = (selectedApi?.restaurants || []).map(id => {
    const found = restaurants.find(r => r._id === id);
    return { id, name: found ? found.name : id };
  });

  // Offer the filter only when it would change what is listed: with no MCP
  // traffic at all it hides everything, and with nothing but MCP traffic it
  // hides nothing, and either way it is a control that does not do anything.
  const mcpCount = items.filter(k => usedWithMcp(usage, k._id)).length;
  const canFilter = mcpCount > 0 && mcpCount < items.length;
  // The selected key is always listed, filter or not. A key created a moment
  // ago has no traffic yet and the effect above selects it, so filtering it
  // out would leave the picker showing a key that is not among its options.
  const pickerKeys =
    canFilter && onlyMcp ? items.filter(k => usedWithMcp(usage, k._id) || k._id === selectedId) : items;
  const selectedKeyUsage = selectedApi && !usage.loading && !usage.error ? usage.byId[selectedApi._id] : null;

  return (
    <>
      <div className="flex-l-r-center m-b-7 m-t-10">
        <div>
          <h2>MCP</h2>
          <p className="small text-muted m-t-2">
            Connect an AI tool or agent to this organisation&apos;s live restaurant and menu data.
          </p>
        </div>
        {canCreate && (
          <Button color="primary" round={true} size="xs" onClick={onCreate}>
            Create MCP key
          </Button>
        )}
      </div>

      {/* Read-only: a key's restaurants are set where the key is edited, in
          API Keys above, and two places to change one thing is how they end
          up disagreeing. */}
      {showingRestaurants && selectedApi && (
        <Modal width="sm" active={true} close={() => setShowingRestaurants(false)}>
          <ModalContent>
            <h4 className="m-b-1">Restaurants</h4>
            <p className="small text-muted">
              {selectedApi.name} can read these restaurants. Change them by editing the key in API Keys.
            </p>
          </ModalContent>
          <ModalContent>
            {scopedRestaurants.length === 0 ? (
              <BlockInfo>This key can&apos;t access any restaurant yet.</BlockInfo>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6 }}>
                {scopedRestaurants.map(r => (
                  <Tag key={r.id} className="cursor-text">
                    {r.name}
                  </Tag>
                ))}
              </div>
            )}
          </ModalContent>
        </Modal>
      )}

      {keyCharting && selectedApi && (
        <UsageChart
          subject={{ api_id: selectedApi._id }}
          title={selectedApi.name}
          fetchSeries={fetchSeries}
          onClose={() => setKeyCharting(false)}
        />
      )}

      <div className="bg-white round-sm bsm-1 p-6 m-b-10">
        {/* Both halves need this, so it sits above them - and outside the
            key list, or an account with no keys would see no URL at all. */}
        <h3 className="m-b-4">Server URL</h3>
        <CopyRow value={MCP_URL} />

        <div className="m-t-10">
          <h3 className="m-b-2">API key</h3>
          {/* The counterpart to the connected-applications note below: the
              two sections are the two ways in, and which one a tool uses
              is decided by the tool, not by the owner. */}
          <p className="small text-muted m-b-6">
            A key to paste into a tool that can&apos;t sign in with a CloudWaitress account.
          </p>

          {items.length === 0 && (
            <div className="text-center">
              <BlockInfo className="flex-center max200 center">
                No API keys yet. Create one to connect an AI tool.
              </BlockInfo>
            </div>
          )}

          {items.length > 0 && (
            <>
              <FormGroup no_border={true} title="Choose your key">
                <SelectAdv
                  type="single"
                  options={pickerKeys.map(k => ({ value: k._id, label: keyOptionLabel(k, restaurants) }))}
                  value={selectedId || ''}
                  onChange={(option: string) => setSelectedId(option)}
                />
                {canFilter && (
                  <div className="m-t-3">
                    <Checkbox
                      id="mcp-only-used-keys"
                      label="Only keys used with MCP in the last 30 days"
                      checked={onlyMcp}
                      onChange={() => setOnlyMcp(v => !v)}
                    />
                  </div>
                )}
                {/* The same line a connected application carries, so the two
                    halves of the page report a key and an app alike. The
                    detail is behind Usage in the gear menu. Nothing is drawn
                    while it loads or if it fails: an absent line is quieter
                    than a line that appears, then moves. */}
                {selectedKeyUsage && (
                  <p
                    className="small text-muted m-t-3"
                    title={
                      selectedKeyUsage.last_call_at ? moment(selectedKeyUsage.last_call_at).format('LLL') : undefined
                    }
                  >
                    {selectedKeyUsage.last_call_at
                      ? `Last used ${moment(selectedKeyUsage.last_call_at).fromNow()} · ${usageSummary(
                          selectedKeyUsage
                        )}`
                      : 'Not used with MCP yet'}
                  </p>
                )}
              </FormGroup>

              {selectedApi && (
                <>
                  {/* The list of restaurants moved into the gear menu, but a
                      key that reaches none of them is broken rather than
                      merely configured, and a warning behind a menu is one
                      nobody reads. */}
                  {scopedRestaurants.length === 0 && (
                    <FormGroup no_border={true}>
                      <BlockInfo>
                        This key can&apos;t access any restaurant yet. Edit it in API Keys and pick at least one.
                      </BlockInfo>
                    </FormGroup>
                  )}

                  <FormGroup
                    no_border={true}
                    help="Paste the key where a tool asks for a bearer token, or copy the whole Authorization header."
                  >
                    {/* The eye stays inline because it acts on the field
                        beside it. Everything else joins one menu, matching
                        the connected-application rows - four controls on a
                        row read as four unrelated things. */}
                    <div className="flex-l-r-center" style={{ gap: 8 }}>
                      <ReadOnlyInput value={shownKey} />
                      <IconButton
                        type="button"
                        color="transparent"
                        disableHoverShadow={true}
                        paddinglr={10}
                        title={showKey ? 'Hide key' : 'Show key'}
                        onClick={() => setShowKey(!showKey)}
                      >
                        {showKey ? <FaEyeSlash /> : <FaEye />}
                      </IconButton>
                      <ActionMenu
                        actions={[
                          { label: 'Copy key', onSelect: () => copyText(apiKey) },
                          {
                            label: 'Copy Authorization header',
                            onSelect: () => copyText(`Authorization: Bearer ${apiKey}`),
                          },
                          { label: 'Restaurants', onSelect: () => setShowingRestaurants(true) },
                          { label: 'Usage', onSelect: () => setKeyCharting(true) },
                        ]}
                      />
                    </div>
                  </FormGroup>

                </>
              )}
            </>
          )}
        </div>

        <div className="m-t-10">
          <h3 className="m-b-2">Connected applications</h3>
          {/* What this list is, and why it is not the one above. The
              immediacy of a disconnect is said in the confirmation, which
              is where someone is deciding whether to do it. */}
          <p className="small text-muted m-b-6">
            Applications signed in with a CloudWaitress account instead of a key.
          </p>
          <ConnectedApps
            fetchConnections={fetchConnections}
            revokeConnection={revokeConnection}
            updateConnectionRestaurants={updateConnectionRestaurants}
            fetchSeries={fetchSeries}
            restaurants={restaurants}
          />
        </div>

        <div className="m-t-10">
          <h3 className="m-b-2">Usage limit</h3>
          <p className="small text-muted m-b-6">
            How many times a restaurant&apos;s data may be read through MCP each day, across every key and
            connected application.
          </p>
          <UsageLimits
            restaurants={restaurants}
            fetchLimits={fetchLimits}
            updateLimits={updateLimits}
            canEdit={canEditLimits}
          />
        </div>
      </div>
    </>
  );
};
